import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaCheck, FaTrash, FaPrint, FaArrowLeft } from 'react-icons/fa';
import api from '../api';
import { theme, t } from '../theme';
import { imprimirEtiquetas } from '../platform/etiqueta';
import StockExit from './StockExit';
import {
  aparenciaDaSituacao,
  copiasParaFornada,
  dataComHora,
  ROTULO_CONSERVACAO,
  textoDoSaldo,
} from './etiquetaValidade';

/**
 * A tela que o QR abre: /e/:codigo
 *
 * ELA EXISTE DESDE A PRIMEIRA VERSÃO, e isso não é opcional. Papel impresso é
 * permanente: não dá para imprimir etiqueta hoje com um link morto e prometer
 * que numa versão futura ele passa a funcionar. Cada QR que sai da impressora
 * é um contrato com um endereço.
 *
 * Funciona lida por qualquer câmera: no celular com o app instalado, o
 * universal link abre aqui dentro; sem o app, abre no navegador. Nos dois
 * casos exige login — a etiqueta é da equipe da cozinha, não da cliente.
 */
const EtiquetaResolver = () => {
  const { codigo } = useParams();
  const navigate = useNavigate();
  const [etiqueta, setEtiqueta] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [baixaAberta, setBaixaAberta] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get(`/api/etiquetas/${codigo}`);
      setEtiqueta(r.data);
      setErro(null);
    } catch (e) {
      const status = e.response?.status;
      // Mensagens específicas de propósito: nenhum destes é um erro do sistema,
      // e um número de status faria a pessoa achar que o app quebrou.
      if (status === 404) setErro('Esta etiqueta não é desta confeitaria.');
      // 403 = módulo desligado. Esta rota fica fora do portão porque papel já
      // impresso não pode virar link morto — mas a API recusa, e a pessoa
      // precisa saber que o caminho é o suporte, não tentar de novo.
      else if (status === 403) setErro('O controle de validade não está ativo nesta conta.');
      else setErro('Não consegui abrir a etiqueta.');
    } finally {
      setCarregando(false);
    }
  }, [codigo]);

  useEffect(() => { carregar(); }, [carregar]);

  const acao = async (caminho, corpo) => {
    setOcupado(true);
    try {
      const r = await api.post(`/api/etiquetas/${codigo}/${caminho}`, corpo || {});
      setEtiqueta(r.data);
      setErro(null);
    } catch (e) {
      setErro(e.response?.data?.erro || 'Não consegui registrar.');
    } finally {
      setOcupado(false);
    }
  };

  if (carregando) {
    return <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textMuted }}>Abrindo etiqueta...</div>;
  }

  if (erro && !etiqueta) {
    return (
      <div style={{ padding: 24, maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
        <p style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text }}>{erro}</p>
        <p style={{ fontSize: 13, color: theme.colors.textMuted, marginBottom: 20 }}>
          Código: <strong>{codigo}</strong>
        </p>
        <button onClick={() => navigate('/etiquetas')} style={t.btnPrimary}>
          <FaArrowLeft /> Ver todas as etiquetas
        </button>
      </div>
    );
  }

  const ap = aparenciaDaSituacao(etiqueta.situacao);
  const saldo = textoDoSaldo(etiqueta);
  const encerrada = etiqueta.status !== 'ativa';

  return (
    <div style={{ padding: 16, maxWidth: 480, margin: '0 auto' }}>
      <div style={{
        background: '#fff', border: `1px solid ${theme.colors.border}`,
        borderTop: `5px solid ${ap.cor}`, borderRadius: 14, padding: 18,
      }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text, lineHeight: 1.2 }}>
          {etiqueta.nome}
        </div>
        <div style={{ fontSize: 13, color: theme.colors.textMuted, marginTop: 4 }}>
          {ROTULO_CONSERVACAO[etiqueta.conservacao] || etiqueta.conservacao}
          {etiqueta.quantidade != null
            && ` · ${String(etiqueta.quantidade).replace('.', ',')}${etiqueta.unidade_medida || ''}`}
        </div>

        <div style={{
          background: ap.fundo, color: ap.cor, borderRadius: 10,
          padding: '10px 12px', marginTop: 14, textAlign: 'center',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {encerrada
              ? (etiqueta.status === 'descartada' ? 'Descartada' : 'Consumida')
              : ap.rotulo}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>
            {dataComHora(etiqueta.validade)}
          </div>
        </div>

        <div style={{ marginTop: 14, fontSize: 13, color: theme.colors.textSecondary, display: 'grid', gap: 4 }}>
          <div>Manipulado em <strong>{dataComHora(etiqueta.manipulado_em)}</strong></div>
          {saldo && <div>Da fornada, <strong>{saldo}</strong></div>}
          {etiqueta.responsavel && <div>Responsável: <strong>{etiqueta.responsavel}</strong></div>}
          {etiqueta.lote_texto && <div>Lote: <strong>{etiqueta.lote_texto}</strong></div>}
          {etiqueta.validade_original && (
            <div style={{ color: theme.colors.textMuted }}>
              Validade original do fabricante: {etiqueta.validade_original}
            </div>
          )}
        </div>

        {erro && (
          <div style={{ background: '#fdecea', color: '#c0392b', padding: 10, borderRadius: 8, marginTop: 12, fontSize: 13 }}>
            {erro}
          </div>
        )}

        {!encerrada && (
          <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
            {/* So faz sentido quando a etiqueta veio de um insumo do estoque.
                Uma embalagem de brigadeiro feita de ficha tecnica nao e Produto e
                nunca sera baixado — o que ele tem e "usei" e "descartei". */}
            {etiqueta.produto_id && (
              <button disabled={ocupado} onClick={() => setBaixaAberta(true)}
                style={{ ...t.btnSecondary, justifyContent: 'center' }}>
                Dar baixa no estoque
              </button>
            )}
            <button disabled={ocupado} onClick={() => acao('consumir', { unidades: 1 })}
              style={{ ...t.btnPrimary, justifyContent: 'center', padding: '12px 0' }}>
              <FaCheck /> {saldo ? 'Usei 1' : 'Acabou'}
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button disabled={ocupado} onClick={() => acao('descartar', { motivo: 'venceu' })}
                style={{ ...t.btnSecondary, flex: 1, justifyContent: 'center', color: theme.colors.danger, borderColor: '#f5b7b1' }}>
                <FaTrash /> Descartar
              </button>
              <button disabled={ocupado}
                onClick={() => imprimirEtiquetas(copiasParaFornada(etiqueta), `etiqueta-${etiqueta.codigo}.pdf`)}
                style={{ ...t.btnSecondary, flex: 1, justifyContent: 'center' }}>
                <FaPrint /> Reimprimir
              </button>
            </div>

      {baixaAberta && (
        // Reaproveita a tela de saida em vez de duplicar a baixa: o FIFO por
        // validade continua sendo do backend, e o rastro de qual etiqueta
        // gerou a saida vai junto.
        <StockExit
          isModal
          produtoSelecionado={{ id: etiqueta.produto_id, nome: etiqueta.nome }}
          etiquetaCodigo={etiqueta.codigo}
          fecharModal={() => { setBaixaAberta(false); carregar(); }}
        />
      )}
          </div>
        )}

        <button onClick={() => navigate('/etiquetas')}
          style={{ ...t.btnSecondary, width: '100%', justifyContent: 'center', marginTop: 10 }}>
          Ver todas as etiquetas
        </button>

        <div style={{ textAlign: 'center', marginTop: 12, fontSize: 11, color: '#bbb' }}>
          {etiqueta.codigo}
        </div>
      </div>
    </div>
  );
};

export default EtiquetaResolver;
